/**
 * usePincodeAutoFill
 *
 * SaaS-grade PIN/ZIP auto-fill hook.
 *
 * India  → uses `india-pincode-search` (local, offline, instant — no API call)
 *          Returns: { state (Title-case), city/district, suggestions[] }
 *
 * International → calls `api.zippopotam.us` (free, 60+ countries, no auth)
 *                 Only public postal-code geography — zero PHI involved.
 *
 * Use-cases covered:
 *  1. PIN/ZIP entered  → auto-fill city + state
 *  2. PIN cleared      → optionally clear city + state
 *  3. City text typed  → suggest matching pincodes  (India only, debounced)
 *  4. Country changes  → reset lookup state
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PincodeSuggestion {
  pincode: string;
  city: string;
  district: string;
  state: string;
  office?: string;
}

export type PincodeStatus = 'idle' | 'loading' | 'success' | 'not_found' | 'error';

export interface PincodeAutoFillResult {
  /** Current lookup status */
  status: PincodeStatus;
  /** Resolved city name (from PIN lookup) */
  resolvedCity: string;
  /** Resolved state name (from PIN lookup) */
  resolvedState: string;
  /** Pincode suggestions for reverse city→pin lookup (India only) */
  pinSuggestions: PincodeSuggestion[];
  /** Call when user types a PIN/ZIP — triggers forward lookup */
  onPincodeChange: (pin: string, countryIso?: string) => void;
  /** Call when user types a city — triggers reverse PIN suggestions (India only) */
  onCityChange: (city: string) => void;
  /** Reset all state (e.g. on country change) */
  reset: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toTitle = (s: string) =>
  s
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// ISO country code → zippopotam.us country slug map (selected countries)
const ISO_TO_ZIPPO: Record<string, string> = {
  US: 'us', GB: 'gb', DE: 'de', FR: 'fr', CA: 'ca', AU: 'au',
  NL: 'nl', BE: 'be', PL: 'pl', SE: 'se', NO: 'no', DK: 'dk',
  FI: 'fi', IE: 'ie', PT: 'pt', ES: 'es', IT: 'it', CH: 'ch',
  AT: 'at', NZ: 'nz', SG: 'sg', JP: 'jp', BR: 'br', MX: 'mx',
  ZA: 'za', PK: 'pk', BD: 'bd', LK: 'lk',
};

// ─── India offline lookup (synchronous) ──────────────────────────────────────

let _indiaDb: any = null;
function getIndiaDb() {
  if (!_indiaDb) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      _indiaDb = require('india-pincode-search');
    } catch {
      _indiaDb = null;
    }
  }
  return _indiaDb;
}

function lookupIndiaPin(pin: string): PincodeSuggestion[] {
  const db = getIndiaDb();
  if (!db) return [];
  const results: any[] = db.search(pin) || [];
  return results.map((r: any) => ({
    pincode: r.pincode,
    city: toTitle(r.city || r.district || ''),
    district: toTitle(r.district || ''),
    state: toTitle(r.state || ''),
    office: r.office,
  }));
}

function searchIndiaByOffice(query: string): PincodeSuggestion[] {
  const db = getIndiaDb();
  if (!db || query.length < 3) return [];
  const results: any[] = db.search(query) || [];
  // Dedupe by pincode
  const seen = new Set<string>();
  return results
    .filter((r: any) => { if (seen.has(r.pincode)) return false; seen.add(r.pincode); return true; })
    .slice(0, 8)
    .map((r: any) => ({
      pincode: r.pincode,
      city: toTitle(r.city || r.district || ''),
      district: toTitle(r.district || ''),
      state: toTitle(r.state || ''),
      office: r.office,
    }));
}

// ─── International lookup via zippopotam.us ───────────────────────────────────

async function lookupZippopotam(
  countrySlug: string,
  zip: string,
): Promise<{ city: string; state: string } | null> {
  try {
    const res = await fetch(`https://api.zippopotam.us/${countrySlug}/${zip}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = data.places?.[0];
    if (!place) return null;
    return {
      city: place['place name'] || '',
      state: place['state'] || place['state abbreviation'] || '',
    };
  } catch {
    return null;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePincodeAutoFill(): PincodeAutoFillResult {
  const [status, setStatus] = useState<PincodeStatus>('idle');
  const [resolvedCity, setResolvedCity] = useState('');
  const [resolvedState, setResolvedState] = useState('');
  const [pinSuggestions, setPinSuggestions] = useState<PincodeSuggestion[]>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setResolvedCity('');
    setResolvedState('');
    setPinSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  // ── Forward: PIN → city + state ───────────────────────────────────────────
  const onPincodeChange = useCallback(
    (pin: string, countryIso: string = 'IN') => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!pin || pin.length < 4) {
        reset();
        return;
      }

      // India: 6-digit PIN, synchronous, instant
      if (countryIso === 'IN') {
        if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
          setStatus('idle');
          return;
        }
        setStatus('loading');
        const results = lookupIndiaPin(pin);
        if (results.length === 0) {
          setStatus('not_found');
          setResolvedCity('');
          setResolvedState('');
          setPinSuggestions([]);
        } else {
          const primary = results[0];
          setResolvedCity(primary.city);
          setResolvedState(primary.state);
          setPinSuggestions(results);
          setStatus('success');
        }
        return;
      }

      // International: debounce then call zippopotam.us
      const slug = ISO_TO_ZIPPO[countryIso];
      if (!slug) {
        setStatus('idle');
        return;
      }

      setStatus('loading');
      debounceRef.current = setTimeout(async () => {
        const result = await lookupZippopotam(slug, pin);
        if (!result) {
          setStatus('not_found');
          setResolvedCity('');
          setResolvedState('');
        } else {
          setResolvedCity(result.city);
          setResolvedState(result.state);
          setStatus('success');
        }
      }, 600);
    },
    [reset],
  );

  // ── Reverse: city text → pin suggestions (India only) ─────────────────────
  const onCityChange = useCallback((city: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!city || city.length < 3) {
      setPinSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      const suggestions = searchIndiaByOffice(city);
      setPinSuggestions(suggestions);
    }, 400);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { status, resolvedCity, resolvedState, pinSuggestions, onPincodeChange, onCityChange, reset };
}
